import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {UserNotification, NotificationsResponse} from '../types'

type InfiniteQueryData = {
    pages: NotificationsResponse[]
    pageParams: (number | undefined)[]
}

/**
 * Helper function to update notifications in all cached queries
 */
export const updateNotificationsInCache = (
    queryClient: ReturnType<typeof useQueryClient>,
    updateFn: (notification: UserNotification) => UserNotification
) => {
    // Update all notification queries using setQueriesData - the proper way
    queryClient.setQueriesData<InfiniteQueryData>(
        {queryKey: [QUERY_KEYS.NOTIFICATIONS]},
        (oldData: InfiniteQueryData | undefined) => {
            if (!oldData?.pages?.length) return oldData
            return {
                ...oldData,
                pages: oldData.pages.map(page => ({
                    ...page,
                    data: page.data.map(updateFn)
                }))
            }
        }
    )
}

/**
 * Helper function to add a new notification to all cached queries
 */
export const addNotificationToCache = (
    queryClient: ReturnType<typeof useQueryClient>,
    newNotification: UserNotification
) => {
    // Update all notification queries using setQueriesData - the proper way
    queryClient.setQueriesData<InfiniteQueryData>(
        {queryKey: [QUERY_KEYS.NOTIFICATIONS]},
        (oldData: InfiniteQueryData | undefined) => {
            if (!oldData?.pages?.length) return oldData
            const newData = {
                ...oldData,
                pages: oldData.pages.map((page, index) => {
                    // Only add to the first page
                    if (index === 0) {
                        return {
                            data: [newNotification, ...page.data],
                            meta: {
                                ...page.meta,
                                total: (page.meta.total || 0) + 1
                            }
                        }
                    }
                    return page
                })
            }
            return newData
        }
    )
}
